import React, { Suspense, useEffect, lazy } from "react";
import "@/styles/global.css";
// Route CSS for lazily loaded pages is not in the SSR head, so the homepage
// and media styles ship with the entry stylesheet (no unstyled first paint).
import "./components/marketing/landing/home/care-home.css";
import { BrandPreview } from "@appcaire/ui/components/brand/BrandPreview";
import { brandPreview } from "./lib/brandPreview";
import "@/styles/navigation.css";
import { Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { DefaultSeo } from "./components/seo/DefaultSeo";
import Layout from "@/components/Layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { Redirects } from "./components/Redirects";
import { NotFound } from "./pages/404";
import { errorTracker } from "./lib/error-tracking";
import { useTranslation } from "react-i18next";
import { useResponseStatus } from "@/lib/ssr-status";
import { getRouteTuple } from "./utils/routes";
import { LogTranslations } from "@/components/site/LogTranslations";
import { initGA, trackPageView } from "./lib/analytics";
import { trackWebVitals } from "./lib/web-vitals";
import { GoogleTagManager } from "./components/GoogleTagManager";
import { Loader2 } from "lucide-react";
import { PUBLIC_HELP_COLLECTIONS } from "@appcaire/shared/help/publicHelpFacets";
import Contact from "@/pages/Contact";
import SaFungerarCaire from "./pages/SaFungerarCaire";

// Every page is a route-level React.lazy chunk so the entry bundle stays
// framework + shell only. SSR still emits full HTML for lazy routes:
// entry-server prerenders with react-dom/static, which waits for these chunks
// before returning markup (renderToString would emit the Suspense fallback).
// NotFound stays eager on purpose — the catch-all must never depend on a
// chunk fetch to render.
const Preview = lazy(() => import("./pages/preview"));
const DesignGallery = lazy(() => import("./pages/DesignGallery"));
const Index = lazy(() => import("./pages/Index"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const FreeScheduleAnalysis = lazy(() => import("@/pages/FreeScheduleAnalysis"));
const CalculatorLandingPage = lazy(
  () => import("./pages/CalculatorLandingPage"),
);
const About = lazy(() => import("@/pages/About"));
const Press = lazy(() => import("@/pages/Press"));
const Integritetspolicy = lazy(() => import("./pages/Integritetspolicy"));
const Villkor = lazy(() => import("./pages/Villkor"));
const Unsubscribe = lazy(() =>
  import("./pages/Unsubscribe").then((module) => ({
    default: module.Unsubscribe,
  })),
);
const Auth = lazy(() => import("./pages/Auth"));
const Careers = lazy(() => import("@/pages/Careers"));
const VadArCaire = lazy(() => import("./pages/VadArCaire"));
const VanligaFragor = lazy(() => import("./pages/vanliga-fragor"));
const SolutionsOverview = lazy(() => import("./pages/For-verksamheten"));
const DiagramPreview = lazy(() =>
  import("./pages/DiagramPreview").then((module) => ({
    default: module.DiagramPreview,
  })),
);
const HelpHubPage = lazy(() =>
  import("./pages/HelpCenter").then((module) => ({
    default: module.HelpHubPage,
  })),
);
const HelpCategoryPage = lazy(() =>
  import("./pages/HelpCenter").then((module) => ({
    default: module.HelpCategoryPage,
  })),
);
const HelpArticlePage = lazy(() =>
  import("./pages/HelpCenter").then((module) => ({
    default: module.HelpArticlePage,
  })),
);
const HelpResourceReferencePage = lazy(() =>
  import("./pages/HelpCenter").then((module) => ({
    default: module.HelpResourceReferencePage,
  })),
);
const HelpStagePage = lazy(() =>
  import("./pages/HelpCenter/HelpListingPages").then((module) => ({
    default: module.HelpStagePage,
  })),
);
const HelpCollectionPage = lazy(() =>
  import("./pages/HelpCenter/HelpListingPages").then((module) => ({
    default: module.HelpCollectionPage,
  })),
);
const HelpAllArticlesPage = lazy(() =>
  import("./pages/HelpCenter/HelpListingPages").then((module) => ({
    default: module.HelpAllArticlesPage,
  })),
);
const HelpDataIndexPage = lazy(() =>
  import("./pages/HelpCenter/HelpListingPages").then((module) => ({
    default: module.HelpDataIndexPage,
  })),
);
const MetricsAndTermsPage = lazy(() =>
  import("./pages/HelpCenter/MetricsAndTermsPage").then((module) => ({
    default: module.MetricsAndTermsPage,
  })),
);

// Lazy load product pages
const Plattform = lazy(() => import("./pages/Plattform"));
const PlatformIntegrations = lazy(() => import("./pages/PlatformIntegrations"));
const ProductImageLibrary = lazy(() => import("./pages/ProductImageLibrary"));
const Pricing = lazy(() => import("./pages/Pricing"));
const MobileAppTourPage = lazy(
  () => import("./pages/Product/MobileAppTourPage"),
);
const DashboardTourPage = lazy(
  () => import("./pages/Product/DashboardTourPage"),
);
const InteractiveDemoPage = lazy(
  () => import("./pages/Product/InteractiveDemoPage"),
);
const ProductFilmPage = lazy(() => import("./pages/Product/ProductFilmPage"));
const InvestorAiOsPage = lazy(
  () => import("./pages/Investors/InvestorAiOsPage"),
);
const CareersAgenticWorkflow = lazy(
  () => import("./pages/CareersAgenticWorkflow"),
);
const HeadOfAiPage = lazy(() => import("./pages/careers/HeadOfAiPage"));
const CustomerSuccessManagerPage = lazy(
  () => import("./pages/careers/CustomerSuccessManagerPage"),
);

// Lazy load feature pages (not needed on initial load)
const Scheduling = lazy(() => import("./pages/Features/Scheduling"));
const AiChat = lazy(() => import("./pages/Features/AiChat"));
const Analytics = lazy(() => import("./pages/Features/Analytics"));
const Reports = lazy(() => import("./pages/Features/Reports"));
const CarePlans = lazy(() => import("./pages/Features/CarePlans"));
const CompareProposals = lazy(
  () => import("./pages/Features/CompareProposals"),
);
const Handbook = lazy(() => import("./pages/Features/Handbook"));
const MobileApps = lazy(() => import("./pages/Features/MobileApps"));
const SecurityCompliancePage = lazy(
  () => import("./pages/Features/SecurityCompliancePage"),
);
const Onboarding = lazy(() => import("./pages/Features/Onboarding"));
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const DpaReadingPage = lazy(() => import("./pages/DpaReadingPage"));
const EuAiActPage = lazy(() => import("./pages/EuAiActPage"));
const AiStaffPlanning = lazy(() => import("./pages/Features/AiStaffPlanning"));
const RouteOptimizationHomeCare = lazy(
  () => import("./pages/Features/RouteOptimizationHomeCare"),
);

// Lazy load solution pages
const SchedulerPersona = lazy(
  () => import("./pages/Solutions/SchedulerPersona"),
);
const OperationsManagerPersona = lazy(
  () => import("./pages/Solutions/OperationsManagerPersona"),
);
const PrivateHomeCare = lazy(() => import("./pages/Solutions/PrivateHomeCare"));
const HomeCareChain = lazy(() => import("./pages/Solutions/HomeCareChain"));
const Municipalities = lazy(() => import("./pages/Solutions/Municipalities"));
const Caregivers = lazy(() => import("./pages/Solutions/Caregivers"));

// Lazy load resource pages (large, rarely visited on first load)
const Resurser = lazy(() => import("./pages/Resurser"));
const FutureHomecareTrendsArticlePage = lazy(
  () => import("./pages/Resources/articles/future-homecare-trends"),
);
const JamforelseSchemalaggningssystemPage = lazy(
  () => import("./pages/Resurser/jamforelse-schemalaggningssystem"),
);
const GuidesPage = lazy(() => import("./pages/Resurser/Guides"));
const ArticlesPage = lazy(() => import("./pages/Resurser/Articles"));
const NewsPage = lazy(() => import("./pages/Resurser/News"));
const DevelopersPage = lazy(() => import("./pages/Developers"));
const BlogPage = lazy(() => import("./pages/Blog"));
const ProductNewsArticlePage = lazy(
  () => import("./pages/Resurser/ProductNewsArticle"),
);
const ComparisonsPage = lazy(() => import("./pages/Resurser/Comparisons"));
const RoutePatternsVsDynamic = lazy(
  () => import("./pages/Resurser/RoutePatternsVsDynamic"),
);
const ExcelVsAiPage = lazy(() => import("./pages/Resurser/ExcelVsAiPage"));
const ComprehensiveAiImplementationGuideComponent = lazy(
  () => import("./pages/Resources/guides/ComprehensiveAiImplementationGuide"),
);
const EffectiveStaffPlanningHomeCare = lazy(
  () => import("./pages/Resources/guides/effective-staff-planning-home-care"),
);
const OptimizeHomeCareSchedulePage = lazy(
  () => import("./pages/Resources/guides/optimize-home-care-schedule"),
);
const ComprehensiveAiInHomeCareArticlePage = lazy(
  () => import("./pages/Resources/articles/comprehensive-ai-in-home-care"),
);
const OptimizationContinuityAlgorithmsArticlePage = lazy(
  () => import("./pages/Resources/articles/optimization-continuity-algorithms"),
);
const VrptwHomeCareOptimizationArticlePage = lazy(
  () => import("./pages/Resources/articles/vrptw-home-care-optimization"),
);
const BalancingEfficiencyContinuityArticlePage = lazy(
  () => import("./pages/Resources/articles/balancing-efficiency-continuity"),
);
const KontinuitetEffektivitetAIArticlePage = lazy(
  () => import("./pages/Resources/articles/kontinuitet-effektivitet-ai"),
);
const ConstraintManagementHomeCareSchedulingArticlePage = lazy(
  () =>
    import("./pages/Resources/articles/constraint-management-homecare-scheduling"),
);
const Efficiency80HemtjanstArticlePage = lazy(
  () => import("./pages/Resources/articles/efficiency-80-hemtjanst"),
);
const RoutingScienceHomeCareArticlePage = lazy(
  () => import("./pages/Resources/articles/routing-science-home-care"),
);
const SchemaoptimeringBrukartidRestidArticlePage = lazy(
  () => import("./pages/Resources/articles/schemaoptimering-brukartid-restid"),
);
const MissionAfterMsArticlePage = lazy(
  () => import("./pages/Resources/articles/mission-efter-ms"),
);
const DayInHomeCare2026ArticlePage = lazy(
  () => import("./pages/Resources/articles/en-dag-i-hemtjansten-2026"),
);
const SwedishSeoArticlePage = lazy(
  () => import("./pages/Resources/articles/swedish-seo-articles"),
);

// Loading fallback
const PageLoader = () => (
  <div
    className="flex min-h-screen items-center justify-center bg-caire-page text-caire-ink"
    role="status"
    aria-live="polite"
  >
    <Loader2 className="h-8 w-8 animate-spin text-coral-text" />
    <span className="sr-only">Loading...</span>
  </div>
);

// English pages
// import HomeCareSchedulingToolsComparisonPage from './pages/Resources/comparisons/home-care-scheduling-tools-comparison';

// QueryClient is now created in entry-client.tsx and entry-server.tsx

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    errorTracker.trackError({
      message: error.message,
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      type: "error",
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error }: { error?: Error }) => {
  const { t } = useTranslation(["common"]);

  // A caught render error is a transient failure of THIS render, not a
  // statement about the document. It must never emit `noindex`: the boundary
  // wraps every lazy route, so one failed chunk fetch in Googlebot's renderer
  // (assets are immutable+hashed, and a deploy deletes the old ones) would put
  // `noindex,nofollow` in the rendered DOM and deindex a healthy page — Search
  // Console reported exactly that as `BLOCKED_BY_META_TAG` on pages whose SSR
  // HTML was clean.
  //
  // Report 500 when this fallback is what SSR renders. `useResponseStatus` is a
  // no-op on the client, so a client-only hiccup leaves the indexed document
  // untouched. Measured caveat (2026-08-08): a *lazy route* that throws under
  // `prerenderToNodeStream` never reaches this fallback at all — SSR returns 200
  // with a shell-only body instead, so this line does not cover that path. That
  // gap is pre-existing and tracked separately; see
  // src/test/ssr-error-boundary-status.test.tsx.
  //
  // `data-nosnippet` keeps Google from quoting this UI as the SERP snippet when
  // Googlebot's JS renderer hits a stale hashed chunk after deploy (2026-09-09:
  // homepage snippet was "Något gick fel. Försök uppdatera. Uppdatera." while
  // SSR meta + body were healthy). Do not put `noindex` back — see above.
  useResponseStatus(500);

  return (
    <div
      data-nosnippet
      className="flex min-h-screen items-center justify-center bg-caire-page p-4 text-caire-ink"
    >
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-bold text-caire-ink">
          {t("error.somethingWentWrong")}
        </h1>
        <p className="text-[var(--cb-muted)]">{t("error.tryRefreshing")}</p>
        {error && process.env.NODE_ENV === "development" && (
          <details className="rounded-lg border border-caire-hairline bg-white p-3 text-left text-sm text-[var(--cb-muted)]">
            <summary className="cursor-pointer text-caire-ink">
              Error Details
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-[#fff7f1] p-2 text-xs text-caire-ink">
              {error.message}
            </pre>
          </details>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-[var(--cb-action)] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-caire-action-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
        >
          {t("error.refresh")}
        </button>
      </div>
    </div>
  );
};

const LayoutWrapper = () => {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

type BusinessPageProps = {
  language?: string;
};

const withLanguage = (Component: React.ComponentType<BusinessPageProps>) => {
  return ({ language = "sv" }: BusinessPageProps) => (
    <Suspense fallback={<PageLoader />}>
      <Component language={language} />
    </Suspense>
  );
};

const withUnusedLanguage = (Component: React.ComponentType) => {
  return () => (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

/**
 * Component to set language based on URL and track page views
 * Only runs analytics on client side
 */
const LanguageSetter = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Set language based on URL path
    const pathLang = location.pathname.startsWith("/en") ? "en" : "sv";
    if (i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return null;
};

/**
 * Main App component
 * Router, HelmetProvider, QueryClientProvider, and TooltipProvider are provided by entry files
 */
const App = () => {
  const { i18n } = useTranslation();
  const showTranslationDebug =
    import.meta.env.DEV && import.meta.env.VITE_SHOW_TRANSLATION_DEBUG === "1";

  useEffect(() => {
    // Only run analytics on client
    if (typeof window === "undefined") return;

    // Initialize analytics early but after DOM is ready
    const initializeAnalytics = () => {
      initGA();
      trackWebVitals();
    };

    // Small delay to ensure consent defaults are properly set
    const timeoutId = setTimeout(initializeAnalytics, 50);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <ErrorBoundary>
      <GoogleTagManager />
      <ScrollToTop />
      <LanguageSetter />
      <DefaultSeo />
      <Redirects />
      <Routes>
        <Route
          path="/"
          element={
            <Layout>
              {/* Blanket boundary for the lazy route chunks: routes rendered
                  without a withLanguage/withUnusedLanguage wrapper (help
                  center, preview, auth) suspend here. Keep dedicated routes as
                  bare `element={<Component .../>}` — generate-sitemap.js reads
                  that shape to attribute content files to routes. */}
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </Layout>
          }
        >
          <Route path="/preview" element={<Preview />} />
          <Route path="/__design" element={<DesignGallery />} />
          <Route path="/diagrams-preview" element={<DiagramPreview />} />
          <Route path="/auth" element={<Auth />} />

          {(
            [
              ["home", withLanguage(Index)],
              ["vadArCaire", withUnusedLanguage(VadArCaire)],
              ["saFungerarCaire", withUnusedLanguage(SaFungerarCaire)],
              ["plattform", withUnusedLanguage(Plattform)],
              ["contact", withUnusedLanguage(Contact)],
              ["bookDemo", withUnusedLanguage(BookDemo)],
              ["freeAnalysis", withUnusedLanguage(FreeScheduleAnalysis)],
              ["calculator", withUnusedLanguage(CalculatorLandingPage)],
              ["solutions.index", withLanguage(SolutionsOverview)],
              ["solutions.scheduler", withLanguage(SchedulerPersona)],
              ["solutions.private", withLanguage(PrivateHomeCare)],
              ["solutions.chain", withLanguage(HomeCareChain)],
              ["solutions.operations", withLanguage(OperationsManagerPersona)],
              ["solutions.municipalities", withLanguage(Municipalities)],
              ["solutions.caregivers", withLanguage(Caregivers)],
              ["product.mobileAppTour", withLanguage(MobileAppTourPage)],
              ["product.dashboardTour", withLanguage(DashboardTourPage)],
              ["product.interactiveDemo", withLanguage(InteractiveDemoPage)],
              ["product.film", withLanguage(ProductFilmPage)],
              [
                "features.securityCompliance",
                withLanguage(SecurityCompliancePage),
              ],
              ["investors.aiOsBusiness", withLanguage(InvestorAiOsPage)],
              ["features.scheduling", withUnusedLanguage(Scheduling)],
              ["features.aiScheduling", withUnusedLanguage(Scheduling)],
              ["features.aiChat", withUnusedLanguage(AiChat)],
              ["features.analytics", withUnusedLanguage(Analytics)],
              ["features.reports", withUnusedLanguage(Reports)],
              ["features.carePlans", withUnusedLanguage(CarePlans)],
              [
                "features.compareProposals",
                withUnusedLanguage(CompareProposals),
              ],
              ["features.mobileApps", withUnusedLanguage(MobileApps)],
              ["features.handbook", withUnusedLanguage(Handbook)],
              ["features.onboarding", withUnusedLanguage(Onboarding)],
              ["features.aiStaffPlanning", withUnusedLanguage(AiStaffPlanning)],
              [
                "features.routeOptimizationHomeCare",
                withUnusedLanguage(RouteOptimizationHomeCare),
              ],
              ["about", withUnusedLanguage(About)],
              ["press", withUnusedLanguage(Press)],
              ["careers", withUnusedLanguage(Careers)],
              ["faq", withUnusedLanguage(VanligaFragor)],
              ["trust", withLanguage(TrustCenter)],
              ["trustDpa", withLanguage(DpaReadingPage)],
              ["trustAiAct", withLanguage(EuAiActPage)],
              ["pricing", withUnusedLanguage(Pricing)],
              [
                "platform.integrations",
                withUnusedLanguage(PlatformIntegrations),
              ],
              [
                "platform.imageLibrary",
                withUnusedLanguage(ProductImageLibrary),
              ],
              ["legal.privacy", withUnusedLanguage(Integritetspolicy)],
              ["legal.terms", withUnusedLanguage(Villkor)],
              ["legal.unsubscribe", withUnusedLanguage(Unsubscribe)],
              ["resourcesCategory.index", withUnusedLanguage(Resurser)],
              ["resourcesCategory.blog", withUnusedLanguage(BlogPage)],
              ["resourcesCategory.guides", withUnusedLanguage(GuidesPage)],
              ["resourcesCategory.articles", withUnusedLanguage(ArticlesPage)],
              ["resourcesCategory.news", withUnusedLanguage(NewsPage)],
              [
                "resourcesCategory.developers",
                withUnusedLanguage(DevelopersPage),
              ],
              [
                "resourcesCategory.comparisons",
                withUnusedLanguage(ComparisonsPage),
              ],
              [
                "articles.futureHomeCare",
                withUnusedLanguage(FutureHomecareTrendsArticlePage),
              ],
              [
                "articles.freeScheduleAnalysis",
                withUnusedLanguage(ProductNewsArticlePage),
              ],
              [
                "articles.aiAssistantMcp",
                withUnusedLanguage(ProductNewsArticlePage),
              ],
              [
                "articles.saivoInvestment",
                withUnusedLanguage(ProductNewsArticlePage),
              ],
              [
                "articles.comprehensiveAiInHomeCare",
                withUnusedLanguage(ComprehensiveAiInHomeCareArticlePage),
              ],
              [
                "articles.optimizationContinuityAlgorithms",
                withUnusedLanguage(OptimizationContinuityAlgorithmsArticlePage),
              ],
              [
                "articles.vrptwHomeCareOptimization",
                withUnusedLanguage(VrptwHomeCareOptimizationArticlePage),
              ],
              [
                "articles.balancingEfficiencyContinuity",
                withUnusedLanguage(BalancingEfficiencyContinuityArticlePage),
              ],
              [
                "articles.kontinuitetEffektivitetAI",
                withUnusedLanguage(KontinuitetEffektivitetAIArticlePage),
              ],
              [
                "articles.constraintManagementHomeCareScheduling",
                withUnusedLanguage(
                  ConstraintManagementHomeCareSchedulingArticlePage,
                ),
              ],
              [
                "articles.efficiency80Hemtjanst",
                withUnusedLanguage(Efficiency80HemtjanstArticlePage),
              ],
              [
                "articles.routingScienceHomeCare",
                withUnusedLanguage(RoutingScienceHomeCareArticlePage),
              ],
              [
                "articles.scheduleOptimizationCareTimeTravelTime",
                withUnusedLanguage(SchemaoptimeringBrukartidRestidArticlePage),
              ],
              [
                "articles.missionAfterMs",
                withUnusedLanguage(MissionAfterMsArticlePage),
              ],
              [
                "articles.dayInHomeCare2026",
                withUnusedLanguage(DayInHomeCare2026ArticlePage),
              ],
              [
                "articles.aiSchedulingHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.reduceTravelTimeHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.staffPlanningHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.carefoxAiScheduling",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.unassignedVisitsHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.planningSystemCostHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.ibicHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.employeeHandbookHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.socialDocumentationHomeCare",
                withUnusedLanguage(SwedishSeoArticlePage),
              ],
              [
                "articles.futureHomeCare",
                withUnusedLanguage(FutureHomecareTrendsArticlePage),
              ],
              [
                "guides.effectiveStaffPlanning",
                withUnusedLanguage(EffectiveStaffPlanningHomeCare),
              ],
              [
                "guides.optimizeSchedule",
                withUnusedLanguage(OptimizeHomeCareSchedulePage),
              ],
              [
                "guides.comprehensiveAiImplementation",
                withUnusedLanguage(ComprehensiveAiImplementationGuideComponent),
              ],
              [
                "comparisons.schedulingSystems",
                withUnusedLanguage(JamforelseSchemalaggningssystemPage),
              ],
              ["comparisons.excelVsAI", withUnusedLanguage(ExcelVsAiPage)],
              [
                "comparisons.routePatternsVsDynamic",
                withUnusedLanguage(RoutePatternsVsDynamic),
              ],
              [
                "careersAgenticWorkflow",
                withUnusedLanguage(CareersAgenticWorkflow),
              ],
              ["careerRoles.headOfAi", withLanguage(HeadOfAiPage)],
              [
                "careerRoles.customerSuccessManager",
                withLanguage(CustomerSuccessManagerPage),
              ],
            ] as const
          ).flatMap(([key, render]) => {
            const tuple = getRouteTuple(key);
            if (!tuple) {
              console.error(`Route key not found: ${key}`);
              return [];
            }
            return [
              <Route
                key={tuple[0]}
                path={tuple[0]}
                element={render({ language: "sv" })}
              />,
              <Route
                key={tuple[1]}
                path={tuple[1]}
                element={render({ language: "en" })}
              />,
            ];
          })}

          {/* Add a catch-all route for English nested routes */}

          {/* English resource pages */}
          {/* <Route path="/en/resources/comparisons/home-care-scheduling-tools-comparison" element={<HomeCareSchedulingToolsComparisonPage />} /> */}

          {/* Removed hardcoded route - now handled by dynamic routing from routes.ts */}

          <Route path="/help" element={<Navigate to="/hjalp" replace />} />
          <Route path="/hjalp" element={<HelpHubPage language="sv" />} />
          <Route path="/en/help" element={<HelpHubPage language="en" />} />
          <Route
            path="/hjalp/kategori/:category"
            element={<HelpCategoryPage language="sv" />}
          />
          <Route
            path="/en/help/category/:category"
            element={<HelpCategoryPage language="en" />}
          />
          <Route
            path="/hjalp/matvarden-och-begrepp"
            element={<MetricsAndTermsPage language="sv" />}
          />
          <Route
            path="/en/help/metrics-and-terms"
            element={<MetricsAndTermsPage language="en" />}
          />
          <Route
            path="/hjalp/data/:slug"
            element={<HelpResourceReferencePage language="sv" />}
          />
          <Route
            path="/en/help/data/:slug"
            element={<HelpResourceReferencePage language="en" />}
          />
          <Route
            path="/hjalp/data"
            element={<HelpDataIndexPage language="sv" />}
          />
          <Route
            path="/en/help/data"
            element={<HelpDataIndexPage language="en" />}
          />
          <Route
            path="/hjalp/steg/:stage"
            element={<HelpStagePage language="sv" />}
          />
          <Route
            path="/en/help/steps/:stage"
            element={<HelpStagePage language="en" />}
          />
          <Route
            path="/hjalp/artiklar"
            element={<HelpAllArticlesPage language="sv" />}
          />
          <Route
            path="/en/help/articles"
            element={<HelpAllArticlesPage language="en" />}
          />
          {PUBLIC_HELP_COLLECTIONS.flatMap((collection) =>
            (["sv", "en"] as const).map((language) => (
              <Route
                key={`${collection.id}-${language}`}
                path={`${language === "sv" ? "/hjalp" : "/en/help"}/${collection.slug[language]}`}
                element={
                  <HelpCollectionPage
                    language={language}
                    collection={collection.id}
                  />
                }
              />
            )),
          )}
          <Route
            path="/hjalp/:slug"
            element={<HelpArticlePage language="sv" />}
          />
          <Route
            path="/en/help/:slug"
            element={<HelpArticlePage language="en" />}
          />

          <Route path="/en/*" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      {import.meta.env.DEV ? (
        <BrandPreview
          controller={brandPreview}
          label={
            i18n.language.startsWith("sv")
              ? "Förhandsvisa tema"
              : "Preview theme"
          }
          resetLabel={i18n.language.startsWith("sv") ? "Återställ" : "Reset"}
        />
      ) : null}
      {showTranslationDebug && <LogTranslations />}
    </ErrorBoundary>
  );
};

export default App;
